import type { ReactElement, ReactNode } from "react";

import styles from "./Field.module.css";

/** The marker next to the label of a required field. */
const REQUIRED_MARK = "*";

export interface FieldProps {
  readonly id: string;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly required?: boolean | undefined;
  readonly children: ReactNode;
}

/** The identifier of the hint paragraph of the field `id`. */
export function hintId(id: string): string {
  return `${id}-hint`;
}

/** The identifier of the error paragraph of the field `id`. */
export function errorId(id: string): string {
  return `${id}-error`;
}

/**
 * What the control inside the field must pass as its `describedBy`.
 *
 * The wiring is a function rather than something `Field` injects into its
 * children, because cloning an element to add props is the kind of magic that
 * breaks the first time somebody wraps the control in anything.
 */
export function describedBy(
  id: string,
  descriptions: { readonly hint?: string | undefined; readonly error?: string | undefined },
): string | undefined {
  const ids = [
    descriptions.hint === undefined ? undefined : hintId(id),
    descriptions.error === undefined ? undefined : errorId(id),
  ].filter((entry): entry is string => entry !== undefined);
  return ids.length === 0 ? undefined : ids.join(" ");
}

/**
 * A labelled control with its hint and its error.
 *
 * The label is a real `<label htmlFor>`, so clicking it moves the focus and
 * the accessible name comes from the platform instead of from an attribute
 * somebody has to remember. The error carries `role="alert"`, which is what
 * makes a validation failure reach a screen reader the moment it appears.
 */
export function Field({
  id,
  label,
  hint,
  error,
  required = false,
  children,
}: FieldProps): ReactElement {
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
        {required ? (
          <span className={styles.required} aria-hidden="true">
            {REQUIRED_MARK}
          </span>
        ) : null}
      </label>
      {children}
      {hint === undefined ? null : (
        <p id={hintId(id)} className={styles.hint}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p id={errorId(id)} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
